"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Home, Search, SquareLibrary, Store, User } from "lucide-react";
import { useAuthContext } from "@core/helpers/client/contexts/AuthContext";
import { useWebSiteContext } from "@core/helpers/client/contexts/WebSiteContext";
import { openAuthModal } from "@/hooks/useAuthModal";
import useFixedBarHeight, { FOOTER_NAV_HEIGHT_VAR } from "@/hooks/useFixedBarHeight";
import { SELLER_ACADEMY_HOME_HREF } from "@/components/BreadCrumb";
import { FooterIcons as SharedFooterIcons } from "../FooterNavigationBlog";
import { FOOTER_NAV_BAR_CLASS, footer_nav_item_class } from "../footerNavStyles";

// آیکون فعالِ خانه و پروفایل همان SVGهای نویگیشن فروشگاه و مجله است؛ بقیه
// مثل آیتم «فروشگاه» نویگیشن مجله با خط ضخیم‌تر فعال نشان داده می‌شوند.
const FooterIcons = {
    home: { fill: SharedFooterIcons.home.fill, outline: <Home size={24} /> },
    trainings: { fill: <SquareLibrary size={24} strokeWidth={2.75} />, outline: <SquareLibrary size={24} /> },
    search: { fill: <Search size={24} strokeWidth={2.75} />, outline: <Search size={24} /> },
    shop: { fill: <Store size={24} strokeWidth={2.75} />, outline: <Store size={24} /> },
    profile: { fill: SharedFooterIcons.profile.fill, outline: <User size={24} /> },
};

const safe_decode = (value = "") => {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

// آدرس‌ها از route_names بک‌اند می‌آیند و ممکن است اسلش پایانی نداشته باشند یا
// percent-encoded باشند؛ مقایسه روی نسخه‌ی decode‌شده و بدون اسلش پایانی انجام می‌شود
// و لینک خروجی همیشه با `/` تمام می‌شود (`trailingSlash: true`).
const normalize = (path = "") => safe_decode(path.split("?")[0]).replace(/\/+$/, "") || "/";
const with_trailing_slash = (path = "") => (path.endsWith("/") ? path : `${path}/`);

/**
 * نویگیشن پایین موبایل صفحه‌های سلر آکادمی (فیگما: mobile-bottom-navigation).
 * جستجو از هدر موبایل سلر آکادمی به این نوار منتقل شده است.
 */
const FooterNavigationSellerAcademy = () => {
    const barRef = useFixedBarHeight(FOOTER_NAV_HEIGHT_VAR);
    const pathname = normalize(usePathname() || "");
    const router = useRouter();
    const { is_auth } = useAuthContext();
    const { web_route_names } = useWebSiteContext();

    const home_href = web_route_names?.["seller-academy-home"] || SELLER_ACADEMY_HOME_HREF;
    const all_categories_href = web_route_names?.["seller-academy-all-categories"] || "/all-seller-academy-categories";
    const search_href = web_route_names?.["search-seller-academy"] || "/seller-academys";

    const handleAccountClick = () => {
        if (is_auth) {
            router.push("/account/");
        } else {
            openAuthModal("/account/");
        }
    };

    const navigationItems = [
        { key: "home", title: "خانه", href: home_href, active: pathname === normalize(home_href) },
        { key: "trainings", title: "آموزش‌ها", href: all_categories_href, active: pathname === normalize(all_categories_href) },
        { key: "search", title: "جستجو", href: search_href, active: pathname === normalize(search_href) },
        { key: "shop", title: "فروشگاه", href: "/", active: false },
        { key: "profile", title: "پروفایل", onClick: handleAccountClick, active: pathname.startsWith("/account") },
    ];

    return (
        <nav ref={barRef} aria-label="نویگیشن سلر آکادمی" className={FOOTER_NAV_BAR_CLASS}>
            {navigationItems.map(({ key, title, href, onClick, active }) => {
                const className = footer_nav_item_class(active);
                const content = (
                    <>
                        {active ? FooterIcons[key].fill : FooterIcons[key].outline}
                        <span>{title}</span>
                    </>
                );

                if (onClick) {
                    return (
                        <button key={key} type="button" onClick={onClick} className={className}>
                            {content}
                        </button>
                    );
                }

                return (
                    <Link
                        key={key}
                        href={with_trailing_slash(href)}
                        aria-current={active ? "page" : undefined}
                        className={className}
                    >
                        {content}
                    </Link>
                );
            })}
        </nav>
    );
};

export default FooterNavigationSellerAcademy;
